import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import { Button } from '../../../../../../ui';
import {
  Product,
  SubscriptionFeatureDetails,
} from '../../../../../../utils/types/admin_types';

type FeatureCardProps = {
  product: Product;
  companySize: 'Small' | 'Medium' | 'Large';
  interval: 'month' | 'year';
  currentSubscription: SubscriptionFeatureDetails | undefined;
  img: string;
  description: string;
  cardDetailsUnavailable: boolean;
  isTrialUsed: boolean;
  addSubscription: (priceId: string, trial: boolean) => void;
  cancelSubscription: (featureType: string, cancel: boolean) => void;
};

export const FeatureCard: React.FC<FeatureCardProps> = ({
  product,
  companySize,
  interval,
  currentSubscription,
  img,
  description,
  cardDetailsUnavailable,
  isTrialUsed,
  addSubscription,
  cancelSubscription,
}) => {
  const selectedPrice = useMemo(() => {
    if (!product.prices) return null;
    return product.prices.find(
      (price) =>
        price.companySize === companySize && price.interval === interval,
    );
  }, [product, companySize, interval]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip size="small" label="Active" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />;
      case 'trialing':
        return <Chip size="small" label="Trial" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />;
      default:
        return <Chip size="small" label="Inactive" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />;
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRadius: '12px',
        borderColor: 'divider',
        overflow: 'hidden',
        minHeight: 510,
        width: '100%',
        maxWidth: 380,
        mx: 'auto',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'action.active',
          boxShadow: '0 8px 24px -4px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4, px: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%', textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid',
              borderColor: 'divider',
              mb: 1,
            }}
          >
            <Box
              component="img"
              src={img}
              alt={product.feature + ' image'}
              sx={{ width: 56, height: 56, objectFit: 'contain' }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em', mb: 0.5 }}>
            {product.feature}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', lineHeight: 1.5, mb: 1 }}>
            {description}
          </Typography>
        </Box>
      </Box>

      {/* Details */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', px: 3.5, py: 2 }}>
        {currentSubscription && (
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary', fontWeight: 500 }}>
                Subscription:
              </Typography>
              {getStatusBadge(currentSubscription.status)}
            </Stack>
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Active</Box>{' '}
              {new Date(currentSubscription.currentPeriodStart * 1000).toLocaleDateString()}{' to '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {new Date(currentSubscription.currentPeriodEnd * 1000).toLocaleDateString()}
              </Box>
            </Typography>
            {currentSubscription.cancelAt && (
              <Typography sx={{ fontSize: '0.75rem', color: 'error.main', fontWeight: 600 }}>
                Will cancel on {new Date(currentSubscription.cancelAt * 1000).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Current Plan
          </Typography>
          <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.2 }}>
            {selectedPrice ? `USD ${selectedPrice.unitAmount}` : '--'}
            <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 400, ml: 0.5 }}>
              {interval === 'month' ? '/month' : '/year'}
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* CTA Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 'auto',
        }}
      >
        {!currentSubscription ? (
          <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
            {!isTrialUsed && (
              <Button
                variant="secondary"
                onClick={() => selectedPrice && addSubscription(selectedPrice.priceId, true)}
                label="Start Free Trial"
                width="100%"
              />
            )}
            <Button
              variant="primary"
              onClick={() => selectedPrice && addSubscription(selectedPrice.priceId, false)}
              label="Upgrade Now"
              disabled={cardDetailsUnavailable}
              width="100%"
            />
          </Stack>
        ) : currentSubscription.cancelAt ? (
          <Button
            variant="primary"
            onClick={() => cancelSubscription(product.feature, false)}
            label="Renew Subscription"
            disabled={cardDetailsUnavailable}
            width="100%"
          />
        ) : (
          <Button
            variant="secondary"
            onClick={() => cancelSubscription(product.feature, true)}
            label="Cancel Subscription"
            width="100%"
          />
        )}
      </Box>
    </Paper>
  );
};
